import {
  Button,
  ChipMetadata,
  LinkButton,
  Selector,
  Table,
} from '@pmi/dsm-react';
import {
  AlertWarningOutlineIcon,
  ArrowRightOutlineIcon,
  ConnectionAddOutlineIcon,
  CopyOutlineIcon,
  LinkOutlineIcon,
  MoreVerticalOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { CheckIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';

import { Column, Row } from '@pmi/dsm-react/dist/components/table/types';
import { ReactElement, useEffect, useRef, useState } from 'react';
import UnavailableChipMetadata from '../../shared/components/UnavailableChipMetadata';
import { isPayByUser, mapPaymentArrangementText } from '../../shared/constants';
import { AgreementApiModel } from '../../shared/models/DealModel';
import { ProductApiModel } from '../../shared/models/ProductModel';
import window from '../../shared/window';
import styles from './dashboard.module.scss';
import TotalSpentCell from './TotalSpentCell';

export interface AgreementDetailsTableProps {
  agreements: AgreementApiModel[];
  isDisabledActionButton: () => boolean;
  onAgreementLinkClicked: (deal: AgreementApiModel) => void;
  onAddPeople: (deal: AgreementApiModel) => void;
  onCopyHubLink: () => void;
  onCopyPointOfContact: (deal: AgreementApiModel) => void;
  noTableRow: ReactElement;
}

export default function AgreementDetailsTable({
  agreements,
  onAgreementLinkClicked,
  isDisabledActionButton,
  onAddPeople,
  onCopyHubLink,
  onCopyPointOfContact,
  noTableRow,
}: AgreementDetailsTableProps) {
  const tableContainer = useRef(null);
  const tableColumns: Column[] = [
    {
      accessor: 'agreementNameIDComponent',
      label: 'Agreement',
      sortable: false,
      isCustomComponent: true,
      width: 125,
    },
    {
      accessor: 'productsComponent',
      label: 'Products',
      sortable: false,
      isCustomComponent: true,
      width: 250,
    },
    {
      accessor: 'availableComponent',
      label: 'Available',
      sortable: false,
      isCustomComponent: true,
      width: 75,
    },
    {
      accessor: 'totalSpentComponent',
      label: 'Total Spent',
      sortable: false,
      isCustomComponent: true,
      width: 90,
    },
    {
      accessor: 'redeemedComponent',
      label: 'Redeemed',
      sortable: false,
      isCustomComponent: true,
      width: 70,
    },
    {
      accessor: 'statusComponent',
      label: 'Status',
      sortable: false,
      isCustomComponent: true,
      width: 70,
    },
    {
      accessor: 'actionComponent',
      label: '',
      sortable: false,
      isCustomComponent: true,
      width: 42,
    },
  ];
  const [tableRows, setTableRows] = useState<Row[]>([]);

  const createTableRow = (d: AgreementApiModel) => ({
    agreementNameIDComponent: {
      component: (
        <div>
          {d.dealName?.length > 0 && (
            <span className="d-block">
              <LinkButton
                titleText={d.dealName}
                onClick={() => onAgreementLinkClicked(d)}
                size="sm"
                className={`p-0 fw-normal ${styles.tableAgreementLink}`}
              />
            </span>
          )}
          {d?.dealName?.length > 0 && (
            <span className={`d-block ${styles.agreementIdSmall}`}>{d.id}</span>
          )}
          {d?.dealName?.length === 0 && (
            <span className="d-block">
              <LinkButton
                titleText={d.id}
                onClick={() => onAgreementLinkClicked(d)}
                size="sm"
                className={`p-0 fw-normal ${styles.tableAgreementLink}`}
              />
            </span>
          )}

          {d?.paymentArrangement?.length > 0 && (
            <span className={`d-block ${styles.agreementIdSmall}`}>
              {mapPaymentArrangementText(d)}
            </span>
          )}
        </div>
      ),
    },
    productsComponent: {
      component: (
        <div className="d-blocks">
          {d.products.map((p: ProductApiModel, index) => (
            <span
              className={`d-flex ${index > 0 ? 'mt-3' : ''} `}
              key={`${p.sku}Product-${index.toString()}`}
            >
              {p.retired && <UnavailableChipMetadata />}
              <span
                className={`d-block deal-product ${
                  styles.tableAgreementProducts
                } ${p.retired ? styles.gray600 : null}`}
                data-sku={p.sku}
                key={`${p.sku}Product-${index.toString()}`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: p?.name && p?.name?.length > 0 ? p.name : p.sku,
                }}
              />
            </span>
          ))}
        </div>
      ),
    },
    availableComponent: {
      component: (
        <div className="d-block">
          {d.products.map((p: ProductApiModel, index) => {
            let value;
            if (p.retired) {
              value = 0;
            } else if (isPayByUser(d.paymentArrangement)) {
              value = <span>&#8734;</span>;
            } else {
              value = p.quantityNotRedeemed;
            }
            return (
              <span
                className={`d-block ${
                  index > 0 ? 'mt-3' : ''
                } deal-product-value ${styles.tableAgreementProducts} ${
                  p.retired ? styles.gray600 : null
                }`}
                data-sku={p.sku}
                key={`${p.sku}Available-${index.toString()}`}
              >
                {value}
              </span>
            );
          })}
        </div>
      ),
    },
    redeemedComponent: {
      component: (
        <div className="d-block">
          {d.products.map((p: ProductApiModel, index) => (
            <span
              className={`d-block ${
                index > 0 ? 'mt-3' : ''
              } deal-product-value ${styles.tableAgreementProducts}`}
              data-sku={p.sku}
              key={`${p.sku}Redeemed-${index.toString()}`}
            >
              {p.quantityRedeemed}
            </span>
          ))}
        </div>
      ),
    },
    totalSpentComponent: {
      component: <TotalSpentCell agreement={d} />,
    },
    statusComponent: {
      component: (
        <div className="d-block">
          <span className={`d-block mb-3 ${styles.tableAgreementProducts}`}>
            {d.enabled && (
              <ChipMetadata
                text="Enabled"
                icon={<CheckIcon fontSize="xs" />}
                variant="green"
              />
            )}
            {!d.enabled && (
              <ChipMetadata
                text="Disabled"
                icon={<AlertWarningOutlineIcon fontSize="xs" />}
                variant="red"
              />
            )}
          </span>
        </div>
      ),
    },
    actionComponent: {
      component: (
        <div
          className={`${styles.actionButtonMenu} ${
            d?.products?.length <= 3
              ? styles.actionButtonMenuButtonTopPosition
              : ''
          }`}
        >
          <Selector
            label={<MoreVerticalOutlineIcon fontSize="sm" />}
            className={styles.actionButtonMenuButton}
          >
            <div className="d-block p-2">
              <ul className={`list-unstyled ${styles.actionButtonMenuItems}`}>
                <li data-agreement={d.id}>
                  <Button
                    titleText="Go to Agreement"
                    size="sm"
                    variant="light"
                    icon={ArrowRightOutlineIcon}
                    onClick={() => onAgreementLinkClicked(d)}
                  />
                </li>
                <li data-agreement={d.id}>
                  <Button
                    titleText="Add People"
                    size="sm"
                    variant="light"
                    icon={ConnectionAddOutlineIcon}
                    onClick={() => onAddPeople(d)}
                    disabled={isDisabledActionButton()}
                  />
                </li>
                <li data-agreement={d.id}>
                  <Button
                    titleText="Copy Point of Contact"
                    size="sm"
                    variant="light"
                    icon={CopyOutlineIcon}
                    onClick={() => onCopyPointOfContact(d)}
                  />
                </li>
                <li data-agreement={d.id}>
                  <Button
                    titleText="Copy Partner Link"
                    size="sm"
                    variant="light"
                    icon={LinkOutlineIcon}
                    onClick={onCopyHubLink}
                  />
                </li>
              </ul>
            </div>
          </Selector>
        </div>
      ),
    },
  });
  const productsDataAutoHeight = () => {
    let dealProducts: NodeListOf<any> | null = null;
    if (tableContainer && tableContainer.current) {
      dealProducts =
        tableContainer.current.querySelectorAll('span.deal-product');
    } else {
      dealProducts = document.querySelectorAll(
        `div.${styles.agreementListTableContainer} span.deal-product`,
      );
    }
    if (dealProducts) {
      dealProducts.forEach((d: HTMLElement) => {
        const sku = d?.getAttribute('data-sku');
        const children = tableContainer.current?.querySelectorAll(
          `span.deal-product-value[data-sku="${sku}"]`,
        );
        if (children) {
          children.forEach((c: HTMLElement) => {
            // eslint-disable-next-line no-param-reassign
            c.style.height = `${d.clientHeight}px`;
          });
        }
      });
    }
  };
  useEffect(() => {
    if (tableRows.length > 0) {
      const tableLastColumns: NodeListOf<HTMLTableDataCellElement> =
        document.querySelectorAll(
          `.${styles.agreementListTableContainer} table tr td:last-child`,
        );
      if (tableLastColumns) {
        tableLastColumns.forEach((td: HTMLTableDataCellElement) => {
          if (
            td.querySelector(`.${styles.actionButtonMenuButtonTopPosition}`)
          ) {
            // eslint-disable-next-line no-param-reassign
            td.style.zIndex = `9999999`;
          }
        });
      }
    }
  }, [tableRows]);
  useEffect(() => {
    const rows: Row[] = [];
    if (agreements.length > 0) {
      agreements.map((deal: AgreementApiModel) => {
        rows.push(createTableRow(deal));
        return deal;
      });
    }
    setTableRows(rows);
    const timeout = setTimeout(() => productsDataAutoHeight(), 500);
    window.addEventListener('resize', () => productsDataAutoHeight());
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', () => productsDataAutoHeight());
    };
  }, []);
  return (
    <div
      className={`d-block dashboard-alc ${styles.agreementListTableContainer}`}
      ref={tableContainer}
    >
      <Table
        autoResizeLastCol={false}
        columns={tableColumns}
        rows={tableRows}
        total={agreements.length}
        onResize={() => {
          productsDataAutoHeight();
        }}
        noRecordsRendering={noTableRow}
      />
    </div>
  );
}
